import { useEffect, useState } from 'react';
import { checkNewPassword } from '../api';
import {
  EMAIL_IN_PASSWORD,
  emailInPassword,
  strengthView,
  meterView,
  type ServerVerdict,
  type StrengthView,
} from '../strength-text';
import { MIN_PASSWORD_LENGTH } from '../password';

type Estimator = typeof import('../password-strength');
let loading: Promise<Estimator> | undefined;
const loadEstimator = () => (loading ??= import('../password-strength'));

/** Words of this user / product that make a password easy to guess. */
function userInputs(email: string): string[] {
  const e = email.trim().toLowerCase();
  const [local = '', host = ''] = e.split('@');
  return [e, local, ...local.split(/[._+-]+/), host.split('.')[0] ?? '', 'cloudphone', 'dialflix'].filter((w) => w.length >= 3);
}

/**
 * Strength bar + one tip under a new-password box. The estimate (zxcvbn) is advice;
 * then the server is asked whether it would accept the password (common, email,
 * leaked…) and a refusal shows "Not allowed" with the server's reason.
 */
export function StrengthMeter({ password, email }: { password: string; email: string }) {
  const [view, setView] = useState<StrengthView | null>(null);
  const [verdict, setVerdict] = useState<{ password: string; v: ServerVerdict } | null>(null);
  // the server didn't answer in time for this password: show the estimate
  const [gaveUp, setGaveUp] = useState<string | null>(null);

  useEffect(() => {
    if (!password) {
      setView(null);
      return;
    }
    let live = true;
    if ([...password].length >= MIN_PASSWORD_LENGTH && emailInPassword(password, email)) {
      setView(EMAIL_IN_PASSWORD);
      return;
    }
    const t = setTimeout(() => {
      loadEstimator()
        .then((m) => {
          if (!live) return;
          const r = m.estimate(password, userInputs(email));
          setView(strengthView(password, r.score, r.warning, r.suggestion));
        })
        .catch(() => {
          // the word lists couldn't load (offline?): length advice only
          if (live) setView(strengthView(password, 2));
        });
    }, 120);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [password, email]);

  // ask the server once typing pauses (only for passwords long enough to be sent)
  useEffect(() => {
    if ([...password].length < MIN_PASSWORD_LENGTH) return;
    let live = true;
    const t = setTimeout(() => {
      checkNewPassword(password, email || undefined)
        .then((r) => {
          if (live) setVerdict({ password, v: r.ok ? { ok: true } : { ok: false, message: r.message } });
        })
        .catch(() => {
          if (live) setGaveUp(password); // no answer: the estimate stays; saving still checks
        });
    }, 400);
    const giveUp = setTimeout(() => live && setGaveUp(password), 4000);
    return () => {
      live = false;
      clearTimeout(t);
      clearTimeout(giveUp);
    };
  }, [password, email]);

  const long = [...password].length >= MIN_PASSWORD_LENGTH;
  const current = verdict && verdict.password === password ? verdict.v : null;
  const shown = meterView(view, current, long && !current && gaveUp !== password);
  if (!shown) return null;
  return (
    <div className={`strength strength-${shown.tone}`} aria-live="polite">
      <span className="strength-bars" aria-hidden>
        {[1, 2, 3, 4].map((i) => (
          <span key={i} className={i <= shown.bars ? 'on' : ''} />
        ))}
      </span>
      <span className="strength-text">
        <strong>{shown.label}</strong>
        {shown.tip && <span> · {shown.tip}</span>}
      </span>
    </div>
  );
}
